// src/Pages/Experience.js
import React, { useState } from 'react';
import { DiJava } from 'react-icons/di';
import {
  SiJavascript,
  SiXml,
  SiApache,
  SiPhp,
  SiReact,
  SiBootstrap,
  SiRedux,
  SiSpringboot,
  SiPostgresql,
  SiMongodb,
  SiDocker,
  SiJenkins,
  SiAmazonwebservices,
  SiTypescript,
  SiNextdotjs,
  SiTailwindcss,
  SiDjango,
  SiPython,
} from 'react-icons/si';
import clsx from 'clsx';

export const experienceData = [
  {
    company: "University of Mississippi",
    role: "Software Developer",
    date: "May 2024 – Current",
    icons: [SiJavascript, SiXml, SiApache, DiJava, SiPhp],
    responsibilities: [
      "Contributed to the development and maintenance of the university website using Cascade CMS, JavaScript, XML, Velocity, Apache, Java, PHP, and SQL, creating functional and user-focused designs.",
      "Conducted usability testing and enhanced frontend accessibility in compliance with WCAG 2.0 standards, improving user experience, performance, and interactivity.",
      "Managed end-user web publishing and content management requests using HTML, CSS, JavaScript, Jira, and Smartsheet, ensuring content accuracy, usability, and streamlined workflows.",
      "Maintained data privacy and system security by collaborating with cross-functional teams to ensure platform reliability and compliance with institutional standards.",
      "Collaborated with technical leads and stakeholders to develop and enhance webpages, modify system components, implement event calendars, and integrate chatbot solutions.",
      "Participated in discovery sessions and requirements gathering, translating business needs into technical solutions.",
      "Contributed to full lifecycle web development projects, including Figma design implementation, development, testing, Apache deployment, post-go-live support, issue resolution, and root cause analysis.",
    ],
  },
  {
    company: "Community Dreams Foundation",
    role: "Software Developer Intern",
    date: "April 2024 – May 2024",
    icons: [SiReact, SiJavascript],
    responsibilities: [
      "Implemented client-side form validation and managed component state using React Hooks.",
      "Developed user authentication interfaces, including Login and Signup pages, using React.",
      "Created protected routes to restrict access to authenticated users.",
      "Integrated authentication flows with backend APIs.",
    ],
  },
  {
    company: "Vizva Consultancy Services",
    role: "Software Developer Intern",
    date: "Sept 2023 – March 2024",
    icons: [
      SiReact,
      SiJavascript,
      SiBootstrap,
      SiRedux,
      SiSpringboot,
      DiJava,
      SiPostgresql,
      SiMongodb,
      SiDocker,
      SiJenkins,
      SiAmazonwebservices,
    ],
    responsibilities: [
      "Collaborated with a team to develop a healthcare management platform web application.",
      "Built an interactive and high-performance frontend using React Hooks, JavaScript ES6, Bootstrap, and DOM manipulation.",
      "Implemented UI guidelines following WCAG 2.0 standards to improve accessibility and usability.",
      "Created React components in JSX and used Redux for state management, reducing site loading time by 30 ms.",
      "Developed backend services using Spring Boot, Java, REST APIs, and JSON.",
      "Optimized PostgreSQL and MongoDB queries to improve application efficiency for specific features.",
    ],
  },
  {
    company: "Breathing.ai",
    role: "Full Stack Software Engineer Intern",
    date: "Feb 2023 – May 2023",
    icons: [
      SiTypescript,
      SiReact,
      SiNextdotjs,
      SiTailwindcss,
      SiDjango,
      SiPython,
      SiDocker,
      SiAmazonwebservices,
    ],
    responsibilities: [
      "Collaborated in an agile environment with a team and stakeholders to develop and maintain a website and Chrome extension, contributing to a 10% increase in user onboarding.",
      "Translated Figma mockups and wireframes into responsive UI using TypeScript, React.js, Next.js, and Tailwind CSS.",
      "Implemented a language selection feature with i18n in Next.js.",
      "Integrated machine learning algorithms into custom Django middleware for seamless frontend utilization.",
      "Implemented Python-based microservices with Docker containerization and used AWS services for deployment and scaling.",
      "Quickly adopted and applied new technologies and tools, including Chrome DevTools, TypeScript, and Next.js.",
    ],
  },
];

export default function Experience() {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const exp = experienceData[selectedIdx];

  return (
    <section className="bg-slate-50 py-8 text-[#213448] transition-colors duration-300 dark:bg-slate-900 dark:text-slate-100">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row">
        {/* ← Sidebar */}
        <aside className="mb-4 w-full bg-slate-50 pr-0 dark:bg-slate-900 md:mb-0 md:w-1/4 md:pr-8">
          <ul className="space-y-1 flex md:block flex-row flex-wrap gap-2 md:gap-0">
            {experienceData.map((e, idx) => (
              <li key={`${e.company}-${idx}`} className="w-auto">
                <button
                  onClick={() => setSelectedIdx(idx)}
                  className={clsx(
                    "w-full text-left px-3 py-2 rounded transition font-semibold",
                    idx === selectedIdx
                      ? "border-l-4 border-[#94B4C1] bg-slate-100 text-[#547792] dark:border-[#94B4C1] dark:bg-slate-800 dark:text-[#b8d5e6]"
                      : "text-gray-500 hover:bg-[#F3F7FA] hover:text-[#213448] dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                  )}
                >
                  {e.company}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* → Detail panel */}
        <div className="w-full md:w-3/4 pl-0 md:pl-8">
          <h3 className="text-2xl font-bold text-[#213448] dark:text-slate-100">
            {exp.role} <span className="text-[#547792] dark:text-[#b8d5e6]">@ {exp.company}</span>
          </h3>

          <div className="mt-1 text-sm text-gray-500 dark:text-slate-400">
            <div>{exp.date}</div>
          </div>

          <ul className="mt-6 space-y-4">
            {exp.responsibilities.map((bullet, i) => (
              <li key={i} className="flex items-start">
                <span className="mt-1 mr-2 text-[#94B4C1] dark:text-[#94B4C1]">&#9656;</span>
                <p className="text-gray-700 dark:text-slate-300">{bullet}</p>
              </li>
            ))}
          </ul>

          {/* Tech icons (optional) */}
          {exp.icons && (
            <div className="flex flex-wrap items-center mt-6 gap-3">
              {exp.icons.map((Icon, i) => (
                <span key={i} className="text-gray-500 transition hover:text-orange-500 dark:text-slate-400 dark:hover:text-[#F59E0B]">
                  <Icon size={24} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
